import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "alert",
    "bothError",
    "comment",
    "disabledAction",
    "line",
    "quantityError",
    "reasonError",
    "submit"
  ]

  static values = { mandatoryCommentReasons: Array }

  connect() {
    this.lineTargets.forEach(line => this.setLineSelected(line, this.quantityFor(line) > 0))
    this.updateCommentRequirement()
    this.element.dataset.returnSlipReady = "true"
  }

  disconnect() {
    delete this.element.dataset.returnSlipReady
  }

  toggleLine(event) {
    const checkbox = event.currentTarget
    if (checkbox.disabled || checkbox.classList.contains("disabled")) return

    const line = checkbox.closest(".line-box")
    const selected = !line.classList.contains("active")
    this.setLineSelected(line, selected)
    this.setDropdownValue(line.querySelector(".quantity .dropdownContainer"), selected ? 1 : 0)
    if (!selected) this.setDropdownValue(line.querySelector(".reason .dropdownContainer"), null)
    this.updateCommentRequirement()
  }

  submit(event) {
    if (!this.hasErrors()) return

    event.preventDefault()
    this.scrollToFirstError()
  }

  fieldChanged(event) {
    if (!event.target.matches('input[id$="quantity"], input[id$="reason"]')) return

    this.updateCommentRequirement()
    this.refreshErrors(event.target.closest(".line-box"))
  }

  scrollToFirstError() {
    const error = this.element.querySelector(".dropdownContainer.error")
    this.scrollTo(error?.closest(".line-box") || this.alertTarget)
  }

  scrollToAlert() {
    this.scrollTo(this.alertTarget, 16)
  }

  hasErrors() {
    const errors = new Set()
    let atLeastOne = false

    this.lineTargets.forEach(line => {
      const quantity = this.quantityFor(line)
      const reason = this.reasonFor(line)
      const quantityContainer = line.querySelector(".quantity .dropdownContainer")
      const reasonContainer = line.querySelector(".reason .dropdownContainer")

      quantityContainer?.classList.remove("error")
      reasonContainer?.classList.remove("error")

      if (quantity > 0 && reason.length === 0) {
        reasonContainer?.classList.add("error")
        errors.add("reason")
      }
      if (reason.length > 0 && quantity === 0) {
        quantityContainer?.classList.add("error")
        errors.add("quantity")
      }
      if (quantity > 0 || reason.length > 0) atLeastOne = true
    })

    if (!atLeastOne) {
      errors.add("reason")
      errors.add("quantity")
    }

    this.showErrors(errors)
    return errors.size > 0
  }

  showErrors(errors) {
    const hasErrors = errors.size > 0
    this.alertTarget.hidden = !hasErrors
    this.disabledActionTarget.hidden = !hasErrors
    this.submitTarget.disabled = hasErrors
    this.bothErrorTarget.hidden = errors.size !== 2
    this.reasonErrorTarget.hidden = errors.size !== 1 || !errors.has("reason")
    this.quantityErrorTarget.hidden = errors.size !== 1 || !errors.has("quantity")
  }

  setLineSelected(line, selected) {
    line.classList.toggle("active", selected)
    line.querySelector(".quantity")?.classList.toggle("active", selected)
    line.querySelector(".reason")?.classList.toggle("active", selected)

    const checkbox = line.querySelector(".custom-checkbox")
    checkbox?.classList.toggle("active", selected)
    checkbox?.setAttribute("aria-pressed", selected.toString())
  }

  setDropdownValue(container, value) {
    if (!container) return

    const normalizedValue = value == null ? "" : String(value)
    const input = container.querySelector('input[type="hidden"]')

    if (input) input.value = normalizedValue
    input?.dispatchEvent(new Event("change", { bubbles: true }))
  }

  updateCommentRequirement() {
    const required = this.lineTargets.some(line => this.mandatoryCommentReasonsValue.includes(this.reasonFor(line)))
    this.commentTarget.required = required
  }

  refreshErrors(line) {
    if (line?.querySelector(".dropdownContainer.error") || !this.alertTarget.hidden) this.hasErrors()
  }

  quantityFor(line) {
    return Number.parseInt(line.querySelector('input[id$="quantity"]')?.value, 10) || 0
  }

  reasonFor(line) {
    return line.querySelector('input[id$="reason"]')?.value || ""
  }

  scrollTo(element, extraOffset = 0) {
    if (!element) return

    const headerHeight = document.querySelector("header")?.offsetHeight || 0
    const top = element.getBoundingClientRect().top + window.scrollY - headerHeight - extraOffset
    window.scrollTo({ top, behavior: "smooth" })
  }
}
